import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { Container, Header } from "semantic-ui-react";
import { AppFormsContext } from '../../../cnr/contexts/AppFormsContext';
import { appFormHandlers, appFormInitialState, appFormReducer } from '../../../cnr/reducers/AppFormReducer';
import UiSaveButtons from '../../../components/buttons/UiSaveButtons';
import { isItemReady } from '../../../validation/validation';
import FullPageWrapper from '../../../wrappers/FullPageWrapper';
import Wrapper, { wrapperTypes } from '../../../wrappers/Wrapper';
import NoData from '../../alerts/NoData';
import FormForm from '../../forms/FormForm';
import { AppFormWizard } from './AppFormWizard';
import { useAppForm } from 'global/cnr/contexts/useHelpers/useAppForm';
import { useAppForms } from 'global/cnr/contexts/useHelpers/useAppForms';

export const AppFormContext = createContext();

/**
 * 
 * @param {object} props 
 * @param {enum} - formType: the type of form from the `appFormTypes` enum
 * @param {function} - handleUpdateParentData: function to update the parent component
 * @param {object} - parentData: data from the parent component
 * @param {array} - propIgnores: list of props to ingnore, if any
 * @param {boolean} - useWizard: whether the form should be separated into a wizard
 * @returns Form or Wizard
 */
const AppForm = (props) => {

  const {
    dataPropType,
    _formAttributes,
    _header,
    appFormItems,
    appFormType,
    appFormTypes,
    confirmation,
    css,
    data_parent,
    defaultOptions,
    form_handlers: form_handlers_parent,
    handleCancel,
    handleDelete,
    handlePreview,
    handleSaveCaption,
    handleSaveIcon,
    handleSaveColor,
    handleUpdateParentData,
    ignoreItemStatus,
    keepParent,
    previewCaption,
    propIgnores,
    topperCaption,
    topperCaption2,
    updating,
    useFullPageWrapper,
    useWizard,
    useMvc,
    viewOnly,
    vit,
  } = props

  const { getAppForms } = useAppForms()
  const { getFormProps } = useAppForm()

  // appFormsContext
  const appFormsContext = useContext(AppFormsContext);
  const { appForms_state, appForms_handlers } = appFormsContext ?? {}
  const { appForms } = appForms_state ?? {}

  useEffect(() => {
    if (appForms) return;
    const cb = ({ allAppForms }) => {
      appForms_handlers.handleSet_appForms(allAppForms)
    }
    getAppForms({ callback: cb })
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []);

  // appForms
  const init_state = { appFormType, appFormTypes, appFormItems, propIgnores, vit }
  const [appForm_state, appForm_dispatch] = useReducer(appFormReducer, appFormInitialState(init_state));
  const appForm_handlers = appFormHandlers(appForm_dispatch)

  const { formProps, noProps, data_appForm } = appForm_state ?? {}

  const form_handlers = props.form_handlers ? props.form_handlers : { handleFormDataChange_local: appForm_handlers.handleLocalPropChange }

  const { formPropz } = useMemo(() => {
    const { _formProps } = getFormProps({
      appForms,
      appFormItems,
      appFormType,
      appFormTypes,
      dataPropType,
      propIgnores,
      vit,
      form_handlers,
    })
    return { formPropz: _formProps }
  }, [appForms, appFormType])


  const { viewProps } = formProps ?? {}

  const handleSave = () => props.handleSave(data_appForm)

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!formPropz) return
    if (!appForms) return
    if (initializedRef.current) return;
    initializedRef.current = true;
    appForm_handlers.handleInit_form(init_state, formPropz, data_parent)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [formPropz]);

  /** updates the parent component when the data_appForm changes */
  useEffect(() => {
    if (viewProps && data_appForm) {
      if (keepParent) {
        const dfi = { [appFormType]: data_appForm };
        if (!_.isEqual(dfi, data_parent)) { // Prevent unnecessary updates
          handleUpdateParentData && handleUpdateParentData(dfi, isItemReady(viewProps, dfi), vpCount);
        }
      } else {
        if (!_.isEqual(data_appForm, data_parent)) { // Prevent unnecessary updates
          handleUpdateParentData && handleUpdateParentData(data_appForm, isItemReady(viewProps, data_appForm));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [data_appForm]);

  const form = () => <FormForm
    form_handlers={form_handlers}
    formAttributes={_formAttributes}
    formProps={formProps}
    noSort={true}
    viewOnly={viewOnly}
    ignoreItemStatus={ignoreItemStatus}
  />

  const formView = () => {
    const segs = _.map(viewProps, (vp, key) => {
      const data = data_appForm[key]
      return <Container fluid style={{ padding: '1em' }}>
        <Header as='h4'>{_.startCase(key)}</Header>
        <p>
          {data ? data : 'No ' + _.startCase(key)}
        </p>
      </Container>
    })
    return segs
  }

  const footer = () => <UiSaveButtons
    delete={handleDelete ? { oc: handleDelete } : null}
    preview={handlePreview ? { oc: handlePreview, caption: previewCaption ? previewCaption : null } : null}
    save={{ oc: handleSave, caption: handleSaveCaption ? handleSaveCaption : null, disabled: _.isEmpty(data_appForm), color: handleSaveColor, icon: handleSaveIcon }}
    cancel={{ oc: handleCancel, caption: 'Cancel' }}
  ></UiSaveButtons>

  const formWrapper = () => <Wrapper
    header={_header && _header}
    content={viewOnly ? formView() : form()}
    footer={footer()}
    wrapperType={wrapperTypes.paddedFooter}
    updating={updating}
    confirmation={confirmation}
    css={css}
  />

  const fullPageWrapper = () => <FullPageWrapper
    content={formWrapper()}
    handleCancel={handleCancel}
    topperCaption={topperCaption}
    topperCaption2={topperCaption2}
    updating={updating}
  />

  const content_form = () => {
    if (useWizard) {
      return <AppFormWizard
        form_handlers={form_handlers}
        formData={data_appForm}
        handleCancel={handleCancel}
        topperCaption={topperCaption}
        topperCaption2={topperCaption2}
        updating={updating}
        viewProps={viewProps}
        handlePreview={props.handlePreview}
        handleSave={props.handleSave}
        handleSaveCaption={props.handleSaveCaption}
        useMvc={useMvc}
      />
    } else {
      if (props.handleSave) {
        if (useFullPageWrapper) {
          return fullPageWrapper()
        } else {
          return formWrapper()
        }
      } else {
        return viewOnly ? formView() : form()
      }
    }
  }

  if (formProps && viewProps) {
    return <AppFormContext.Provider value={{ appForm_state, appForm_handlers }}>
      {content_form()}
    </AppFormContext.Provider>
  }

  if (noProps) { return <NoData fullCaption={"No form props have been set for `" + appFormType + "`. Please update the `AppForms`."}></NoData> }

  return <div>No Form Props</div>

}

export default AppForm